import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	ArtFormInput,
	ArtFormOutput,
} from "web_myplann/Portfolio/Forms/ArtForm/artFormSchema";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSwitch } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSwitch";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type ArtFormFieldsProps = {
	control: Control<ArtFormInput, unknown, ArtFormOutput>;
};

export const ArtFormFields = ({ control }: ArtFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	return (
		<>
			<Stack mt={0.8} bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="propertyType"
					label={t(Resources.Portfolio.AddArt.Fields.itemTitle)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="customDisposition"
					label={t(Resources.Portfolio.AddArt.Fields.shortDescription)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddArt.Sections.valueAndDates)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="value"
					type="float"
					label={t(Resources.Portfolio.AddArt.Fields.value)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					suffix="Kč"
				/>
				<FlatRowFormDatePicker
					control={control}
					name="purchaseDate"
					label={t(Resources.Portfolio.AddArt.Fields.purchaseDate)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddArt.Sections.description)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="description"
					label={t(Resources.Portfolio.AddArt.Fields.description)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					rows={1}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddArt.Sections.investment)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="forInvestments"
					label={t(Resources.Portfolio.AddArt.Fields.forInvestments)}
					isLast
				/>
			</Stack>
		</>
	);
};
